import {
  DateGranularity,
  type TDateGranularity,
} from '@dbl/core/date';

import type { IColumnRawInfo } from '@dbl/core/dataSource';

import type {
  IGetTableInfoDto,
  ITableColumnClassDto,
  ITableColumnClassVo,
  ITableColumnDto,
  ITableColumnVo,
  ITableDetailVo,
} from './types';

/**
 * 表 `cycle`（更新周期）→ 合法时间粒度。
 *
 * 选表后 `column_granularity` 直接取自 `cycle`，非法值（如「实时」）会污染筛选 `dateType`，
 * 故在 `getTableInfo` / `batchGetTableInfo` 读路径归一。
 *
 * - `"2week"` / `"2month"`：去掉数字后变成 `week` / `month`（与筛选侧写法对齐）
 * - `"hours"` / `"minute"`：映射为 `hour`
 * - 其余非法值：回退 `day`
 */
export function normalizeTableCycleToLegalGranularity(
  cycle?: string,
): TDateGranularity {
  // "2week" → "week"
  const value = (cycle ?? '').replace(/\d/, '');
  if ((Object.values(DateGranularity) as string[]).includes(value)) {
    return value as TDateGranularity;
  }
  if (value === 'hours' || value === 'minute') {
    return DateGranularity.HOUR;
  }
  return DateGranularity.DAY;
}

/**
 * 清洗 `getTableInfo` Response Dto：不可变拷贝并归一 `cycle`。
 *
 * 供边界层在保留 Dto 形态时先行合法化；与 {@link transformTableInfoDtoToVo} 的 cycle 语义对齐。
 */
export function sanitizeGetTableInfoDto(
  dto: IGetTableInfoDto,
): IGetTableInfoDto {
  return {
    ...dto,
    cycle: normalizeTableCycleToLegalGranularity(dto.cycle),
  };
}

const computeIsCommonDimension = (column: ITableColumnDto): boolean =>
  !column.is_metric &&
  column.dimension_setting === 'system' &&
  column.flag === 0;

const transformTableColumnDtoToVo = (
  column: ITableColumnDto,
): ITableColumnVo => ({
  isCommonDimension:
    computeIsCommonDimension(column) || Boolean(column.is_common_dimension),
  isDimension: column.is_dimension,
  isMetric: column.is_metric,
  isSensitive: column.is_sensitive,
  name: column.name,
  type: column.type,
  originType: column.origin_type,
  sqlExpression: column.sql_expression,
  displayName: column.display_name,
  displayNameEn: column.display_name_en,
  remark: column.remark,
  remarkEn: column.remark_en,
  attribute: column.attribute,
  accuracy: column.accuracy,
  flag: column.flag,
  dimensionSetting: column.dimension_setting,
  dateFormat: column.date_format,
  toDateFormat: column.to_date_format,
});

const transformTableColumnClassDtoToVo = (
  columnClass: ITableColumnClassDto,
): ITableColumnClassVo => ({
  name: columnClass.name,
  nameEn: columnClass.name_en,
  columns: columnClass.columns,
});

/**
 * `getTableInfo` / `batchGetTableInfo` 单表 Response Dto → 前端 Vo。
 *
 * `column_class_list[].columns` 仍为 {@link IColumnRawInfo}（QB 协议，P3 再 camelCase 化）。
 */
export const transformTableInfoDtoToVo = (
  dto: IGetTableInfoDto,
): ITableDetailVo => ({
  tableId: dto.table_id,
  tableCode: dto.table_code,
  tableName: dto.table_name,
  customizeName: dto.customize_name,
  customizeNameEn: dto.customize_name_en,
  tableDesc: dto.table_desc,
  cycle: normalizeTableCycleToLegalGranularity(dto.cycle),
  creator: dto.creator,
  label: dto.label,
  subLabel: dto.sub_label,
  columnClassList: dto.column_class_list?.map(transformTableColumnClassDtoToVo),
  hide: dto.hide,
  info: dto.info,
  driver: dto.driver,
  onlyOneTable: dto.only_one_table,
  columnList: dto.column_list?.map(transformTableColumnDtoToVo),
  databaseUuid: dto.database_uuid,
  kind: dto.kind,
  templateList: dto.template_list,
  dbName: dto.db_name,
  sql: dto.sql,
});

/** 批量 getTableInfoList 响应 Dto → Vo 数组。 */
export const transformTableInfoListDtoToVo = (
  dtoList: IGetTableInfoDto[],
): ITableDetailVo[] => dtoList.map(transformTableInfoDtoToVo);
